// Shared onboarding step metadata. Keep the keys in sync with the backend
// ONBOARDING step builder (campaign/service_onboarding.py).
//
// The step list is DYNAMIC and depends on the campaign usage_type:
//   - base flow:        products -> sites -> price_check -> prepare -> search -> match -> ready
//                       (price_check is a frontend-only settings step; not in backend status)
//   - manufacturer:     adds "map" (MAP import for brand protection) after match
//   - seller (rekabet): adds "pricing" (dynamic pricing); + "map" if show_price_violation_tab is on
//   - marketplace API campaigns (settings.marketplace_api set, Task 3): competitor
//     and pricing data comes straight from the marketplace connection, not
//     scraping, so the site-discovery pipeline (sites/price_check/prepare/search/
//     match) is dropped. Mirrors the wizard's own step list for the same
//     campaigns (see campaignWizardSteps.js wizardStepKeys). Final order:
//     integration -> marketplace_products -> marketplace_price_check -> pricing -> ready
//     (Task 19 inserted marketplace_price_check between product selection and
//     dynamic pricing; it is NOT in campaignWizardSteps.js -- the one-time
//     creation wizard is unaffected, only this persistent onboarding flow).

// label/description use t(key, defaultValue) so the skeleton renders even
// before translation.json keys are added.
export const STEP_CATALOG = {
    settings: {
        key: 'settings', icon: 'pi pi-sliders-h',
        labelKey: 'onboarding.step.settings.label', labelDefault: 'Kampanya Ayarları',
        descKey: 'onboarding.step.settings.desc',
        descDefault: 'Kampanya türü, para birimi ve temel ayarları belirle.',
    },
    scheduled_jobs: {
        key: 'scheduled_jobs', icon: 'pi pi-clock',
        labelKey: 'onboarding.step.scheduled_jobs.label', labelDefault: 'Zamanlanmış İşler',
        descKey: 'onboarding.step.scheduled_jobs.desc',
        descDefault: 'Fiyat kontrolü ve arama işlerinin ne zaman çalışacağını ayarla.',
    },
    products: {
        key: 'products', icon: 'pi pi-box',
        labelKey: 'onboarding.step.products.label', labelDefault: 'Ürünler',
        descKey: 'onboarding.step.products.desc',
        descDefault: 'Karşılaştırmak istediğin ürünleri içeri aktar.',
    },
    // Marketplace API campaigns replace the "products" step with this one --
    // see stepsForUsageType below. Reuses the wizard's mp_connect icon (pi-link)
    // for continuity between the one-time wizard and this persistent page.
    // Credentials only -- product import is the separate "marketplace_products"
    // step right after this one (Task 17).
    integration: {
        key: 'integration', icon: 'pi pi-link',
        labelKey: 'onboarding.step.integration.label', labelDefault: 'Integration',
        descKey: 'onboarding.step.integration.desc',
        descDefault: 'Connect your marketplace account and manage your credentials.',
    },
    // Product import for marketplace API campaigns, split out of "integration"
    // into its own step (Task 17) so Integration stays credentials-only.
    // Visible label matches the legacy "products" step's label in both
    // locales, but uses its own translation keys and STEP_CATALOG entry --
    // legacy campaigns keep the "products" key untouched.
    marketplace_products: {
        key: 'marketplace_products', icon: 'pi pi-box',
        labelKey: 'onboarding.step.marketplace_products.label', labelDefault: 'Products',
        descKey: 'onboarding.step.marketplace_products.desc',
        descDefault: 'Choose which marketplace products to track and reprice.',
    },
    // Admin-configurable buybox refresh cadence for marketplace API campaigns
    // (Task 19), inserted right after "marketplace_products" and before
    // "pricing" -- see stepsForUsageType below. Icon matches the legacy
    // "price_check" step above (pi-clock) for visual continuity, even though
    // the two steps configure unrelated things (scraping schedule vs. API
    // poll cadence).
    marketplace_price_check: {
        key: 'marketplace_price_check', icon: 'pi pi-clock',
        labelKey: 'onboarding.step.marketplace_price_check.label', labelDefault: 'Price Check',
        descKey: 'onboarding.step.marketplace_price_check.desc',
        descDefault: 'See how often buybox prices refresh from the marketplace API.',
    },
    map: {
        key: 'map', icon: 'pi pi-shield',
        labelKey: 'onboarding.step.map.label', labelDefault: 'MAP',
        descKey: 'onboarding.step.map.desc',
        descDefault: 'Marka koruması için ürünlerin MAP (minimum ilan fiyatı) değerlerini içeri aktar.',
    },
    sites: {
        key: 'sites', icon: 'pi pi-globe',
        labelKey: 'onboarding.step.sites.label', labelDefault: 'Siteler',
        descKey: 'onboarding.step.sites.desc',
        descDefault: 'İzlenecek rakip siteleri belirle (kendi sitelerin, Google keşfi veya önerilenler).',
    },
    price_check: {
        key: 'price_check', icon: 'pi pi-clock',
        labelKey: 'onboarding.step.price_check.label', labelDefault: 'Fiyat Kontrolü',
        descKey: 'onboarding.step.price_check.desc',
        descDefault: 'Fiyatların günde kaç kez ve hangi saatlerde kontrol edileceğini belirle.',
    },
    prepare: {
        key: 'prepare', icon: 'pi pi-cog',
        labelKey: 'onboarding.step.prepare.label', labelDefault: 'Hazırlık',
        descKey: 'onboarding.step.prepare.desc',
        descDefault: 'Spider veya site-içi araması olmayan siteler için yapay zekâ ile hazırlık yapılır.',
    },
    search: {
        key: 'search', icon: 'pi pi-search',
        labelKey: 'onboarding.step.search.label', labelDefault: 'Tarama',
        descKey: 'onboarding.step.search.desc',
        descDefault: 'Sitelerde site-içi arama yapılıp öneriler toplanır.',
    },
    match: {
        key: 'match', icon: 'pi pi-link',
        labelKey: 'onboarding.step.match.label', labelDefault: 'Eşleştirme',
        descKey: 'onboarding.step.match.desc',
        descDefault: 'Ürün kodu eşleşenler otomatik bağlanır; belirsizler onayına düşer.',
    },
    pricing: {
        key: 'pricing', icon: 'pi pi-dollar',
        labelKey: 'onboarding.step.pricing.label', labelDefault: 'Dinamik Fiyatlandırma',
        descKey: 'onboarding.step.pricing.desc',
        descDefault: 'Rakip fiyatlarına göre dinamik fiyatlandırma kurallarını yapılandır.',
    },
    ready: {
        key: 'ready', icon: 'pi pi-chart-bar',
        labelKey: 'onboarding.step.ready.label', labelDefault: 'Genel Bakış',
        descKey: 'onboarding.step.ready.desc',
        descDefault: 'İlk rakip karşılaştırman hazır. Yeni ürünler için süreci tekrar çalıştırabilirsin.',
    },
};

// usage_type values (see utils/Constants.js usage_types_values):
//   seller -> competitive, manufacturer -> MAP monitoring / brand protection
// Scraping-only steps: they configure or run the site-discovery pipeline
// (tracked sites, search jobs, candidate matching), which marketplace API
// campaigns don't have — those get competitors/pricing from the marketplace
// connection directly (see Task 7: "API-only by design, no sites, no discovery").
const SCRAPING_ONLY_STEPS = ['sites', 'price_check', 'prepare', 'search', 'match'];

export const stepsForUsageType = (usageType, settings) => {
    const s = settings || {};
    const isMarketplaceApi = !!s.marketplace_api;
    // Marketplace API campaigns get credentials from "integration" and product
    // import from "marketplace_products" right after it, instead of the legacy
    // scraping "products" step (which stays untouched for legacy campaigns).
    // "marketplace_price_check" (Task 19) follows right after: it isn't in
    // SCRAPING_ONLY_STEPS, so it survives the filter below and lands between
    // "marketplace_products" and "pricing" in the final order.
    const order = isMarketplaceApi
        ? ['integration', 'marketplace_products', 'marketplace_price_check']
        : ['products'];
    order.push('sites', 'price_check', 'prepare', 'search', 'match');
    if (usageType === 'manufacturer') order.push('map');
    if (usageType === 'seller') {
        order.push('pricing');
        // Seller campaigns get the MAP step too when the price-violation tab is on.
        if (s.show_price_violation_tab) order.push('map');
    }
    order.push('ready');
    const keys = isMarketplaceApi ? order.filter(k => !SCRAPING_ONLY_STEPS.includes(k)) : order;
    return keys.map(k => STEP_CATALOG[k]);
};

export const stepKeysForUsageType = (usageType, settings) => stepsForUsageType(usageType, settings).map(s => s.key);

// status -> PrimeReact severity / icon mapping for the monitor panel.
export const STATUS_META = {
    done:      {icon: 'pi pi-check-circle',         color: '#22c55e', severity: 'success', labelDefault: 'Tamam'},
    active:    {icon: 'pi pi-spin pi-spinner',       color: '#3b82f6', severity: 'info',    labelDefault: 'Devam ediyor'},
    pending:   {icon: 'pi pi-circle',                color: '#9ca3af', severity: null,      labelDefault: 'Bekliyor'},
    attention: {icon: 'pi pi-exclamation-triangle',  color: '#f59e0b', severity: 'warning', labelDefault: 'Aksiyon gerekli'},
};

export const statusMeta = (status) => STATUS_META[status] || STATUS_META.pending;
